'use client';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import { Product } from '@generated-client/backend';
import { ScrollShadow } from '@heroui/react';
import { SliderArrow } from '@trwl/shared/ui/icons';
import { shareData } from '@trwl/util';
import { preprocessImage } from '@trwl/util/preprocessImage';
import cn from 'classnames';
import Image from 'next/image';
import React, { FC, useCallback, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { FaHeart } from 'react-icons/fa';
import { RiShareBoxLine } from 'react-icons/ri';
import SwiperCore from 'swiper';
import { A11y, EffectFade, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

export interface ProductShoeProps {
  isNew: boolean;
  images: string[];
  product: Product;
  handleLike: () => void;
  liked: boolean;
}

const IMAGE_SIZE = 1200;
export const ProductShoe: FC<ProductShoeProps> = ({
  isNew,
  images,
  product,
  handleLike,
  liked,
}) => {
  const swiperRef = useRef<SwiperCore>(null);
  const [index, setIndex] = useState(swiperRef.current?.activeIndex ?? 0);

  const onShareClick = useCallback(() => {
    const link = window?.location?.href;
    shareData({
      title: product.title,
      text: `Посмотри товар "${product.title}" на trwl:`,
      url: link,
    });
  }, [product]);

  return (
    <div className="max-w-[605px] w-full flex max-[662px]:flex-col flex-row gap-[20px] aspect-square max-[662px]:aspect-auto relative">
      <ScrollShadow
        className={cn(
          'h-full w-full max-[662px]:hidden',
          'max-h-full max-w-[70px] min-w-[70px] overflow-y-scroll overflow-x-hidden',
          'max-[662px]:max-h-[70px] max-[662px]:max-w-full max-[662px]:overflow-y-hidden max-[662px]:overflow-x-scroll',
        )}
        size={0}
        hideScrollBar
      >
        <div className="flex flex-col max-[662px]:flex-row gap-[8px]">
          {images.map((image, key) => (
            <div
              key={key}
              className={`w-[65px] h-[65px] min-h-[65px] min-w-[65px] max-w-[65px] max-h-[65px] transition rounded-[4px] cursor-pointer ${
                index === key && 'border-[1px] border-gray-300'
              }`}
              onClick={() => swiperRef.current?.slideTo(key)}
            >
              <Image
                className={'p-0.5'}
                src={preprocessImage(image, IMAGE_SIZE)}
                width={IMAGE_SIZE}
                height={IMAGE_SIZE}
                alt={product.title}
              />
            </div>
          ))}
        </div>
      </ScrollShadow>
      <div
        className={
          'absolute z-40 left-0 bottom-2 max-[662px]:block hidden w-full'
        }
      >
        <div className={'flex flex-row w-full gap-1'}>
          {new Array(images.length).fill(0).map((_, i) => (
            <div
              key={i}
              onClick={() => swiperRef.current?.slideTo(i)}
              className={cn(
                'w-full h-0.5 hover:bg-[#696969] rounded-full duration-300 cursor-pointer',
                i === index ? 'bg-[#696969]' : 'bg-[#dbdbdb]',
              )}
            />
          ))}
        </div>
      </div>
      <div className="flex h-full w-full max-[662px]:max-w-full max-w-[520px] relative max-[662px]:py-0 py-[50px]">
        <Swiper
          className="w-full aspect-square"
          wrapperClass="w-full h-full flex flex-row max-w-full"
          modules={[A11y, Navigation, EffectFade]}
          spaceBetween={0}
          slidesPerView={1}
          effect="fade"
          onSlideChange={(slide) => {
            setIndex(slide.activeIndex);
          }}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
          }}
        >
          {images.map((image, key) => (
            <SwiperSlide
              key={key}
              className="w-full h-full bg-white rounded-[8px]"
            >
              <Image
                src={preprocessImage(image, IMAGE_SIZE)}
                width={IMAGE_SIZE}
                height={IMAGE_SIZE}
                alt={product.title}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {isNew && (
          <div className="absolute z-30 top-[20px] left-[20px] py-[7px] px-[14px] bg-[#A9D0CD] text-white text-[16px] font-[400]">
            Новинка
          </div>
        )}

        <div
          className={
            'absolute top-4 right-1 z-30 w-[30px] flex flex-col items-center gap-4'
          }
        >
          <div className={'relative w-full h-[30px]'}>
            <div
              className={
                'absolute w-full aspect-square flex items-center justify-center cursor-pointer top-0 left-0'
              }
              onClick={handleLike}
            >
              {!liked && (
                <FaHeart
                  className="transition animate-ping w-[20px] h-[20px]"
                  style={{
                    color: liked ? '#DE392E' : '#6F6F6F',
                  }}
                />
              )}
            </div>
            <div
              className={
                'absolute w-full aspect-square flex items-center justify-center cursor-pointer top-0 left-0'
              }
              onClick={handleLike}
            >
              <FaHeart
                className="transition w-[23px] h-[23px]"
                style={{
                  color: liked ? '#DE392E' : '#6F6F6F',
                }}
              />
            </div>
          </div>
          {isMobile && (
            <div
              className={
                'w-full aspect-square flex items-center justify-center '
              }
              onClick={onShareClick}
            >
              <RiShareBoxLine
                className={'cursor-pointer fill-[#232323]'}
                size={25}
              />
            </div>
          )}
        </div>

        <div className="absolute hidden lg:flex flex-row bottom-2 gap-[8px] right-[25px] z-30">
          <button
            onClick={() => swiperRef.current?.slidePrev()}
            className={cn(
              'w-[40px] h-[40px] rounded-full',
              'hidden md:flex items-center justify-center',
              'hover:bg-[#f5f5f5] duration-300',
            )}
          >
            <SliderArrow className="rotate-[180deg] pointer-events-none" />
          </button>

          <button
            onClick={() => swiperRef.current?.slideNext()}
            className={cn(
              'w-[40px] h-[40px] rounded-full',
              'hidden md:flex items-center justify-center',
              'hover:bg-[#f5f5f5] duration-300',
            )}
          >
            <SliderArrow className="pointer-events-none" />
          </button>
        </div>
      </div>
    </div>
  );
};
